import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, 
  Calendar, 
  Receipt, 
  Target, 
  TrendingUp, 
  Lightbulb, 
  BookOpen, 
  Settings, 
  LogOut,
  Menu,
  X,
  Crown,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ecaa2978-2f20-40e3-8453-4248c3146dce/artifacts/mfi4q0wb_7710DE4F-B183-47BF-A0BF-17D901748F49.png";

const navItems = [
  { path: "/calendar", icon: Calendar, label: "Calendar" },
  { path: "/add", icon: Plus, label: "Add", highlight: true },
  { path: "/dashboard", icon: LayoutDashboard, label: "Overview" },
  { path: "/bills", icon: Receipt, label: "Bills" },
  { path: "/goals", icon: Target, label: "Goals" },
  { path: "/scenarios", icon: TrendingUp, label: "Scenarios" },
  { path: "/insights", icon: Lightbulb, label: "Insights", premium: true },
  { path: "/education", icon: BookOpen, label: "Learn" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export const Sidebar = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleLogout = () => {
    logout(navigate);
  };

  return (
    <div className="min-h-screen bg-p2p-bg flex">
      {/* Desktop Sidebar - Light Theme */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-p2p-bg border-r border-p2p-border fixed h-full z-40">
        <div className="p-6">
          <Link to="/calendar" className="block" data-testid="sidebar-logo">
            <img src={LOGO_URL} alt="Path2Plenty" className="h-14 w-auto" />
          </Link>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${location.pathname === item.path ? 'active' : ''} ${item.highlight ? 'bg-p2p-gold/10 border border-p2p-gold/30' : ''}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className={`w-5 h-5 ${item.highlight ? 'text-p2p-gold' : ''}`} strokeWidth={1.5} />
              <span className={item.highlight ? 'text-p2p-gold font-medium' : ''}>{item.label}</span>
              {item.premium && !user?.is_premium && (
                <Crown className="w-4 h-4 text-p2p-gold ml-auto" />
              )}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-p2p-border">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-p2p-teal/10 flex items-center justify-center">
                <span className="text-p2p-teal font-semibold">{user?.name?.[0]?.toUpperCase()}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-p2p-teal truncate">{user?.name}</p>
              <p className="text-xs text-p2p-muted truncate">{user?.email}</p>
            </div>
          </div>
          
          {user?.is_premium && (
            <div className="mx-4 mb-3 px-3 py-1.5 rounded-full bg-p2p-gold/10 border border-p2p-gold/20 text-center">
              <span className="text-xs text-p2p-gold font-medium flex items-center justify-center gap-1">
                <Crown className="w-3 h-3" /> Premium
              </span>
            </div>
          )}

          <Button
            variant="ghost"
            className="w-full justify-start text-p2p-muted hover:text-p2p-error hover:bg-p2p-error/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header - Light Theme */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-p2p-border">
        <div className="flex items-center justify-between p-4">
          <Link to="/calendar">
            <img src={LOGO_URL} alt="Path2Plenty" className="h-11 w-auto" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-p2p-teal"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md border-b border-p2p-border p-4 shadow-lg">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                  {item.premium && !user?.is_premium && (
                    <Crown className="w-4 h-4 text-p2p-gold ml-auto" />
                  )}
                </Link>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-p2p-border">
              <Button
                variant="ghost"
                className="w-full justify-start text-p2p-muted hover:text-p2p-error"
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              >
                <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Sign Out
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Nav - Light Theme */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-p2p-border">
        <div className="flex items-center justify-around py-2">
          {navItems.slice(0, 5).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                location.pathname === item.path 
                  ? 'text-p2p-teal bg-p2p-teal/5' 
                  : 'text-p2p-muted hover:text-p2p-teal'
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[10px] mt-1 font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 pb-20 lg:pb-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
    <div>
      <h1 className="font-manrope text-2xl sm:text-3xl font-semibold text-p2p-teal">{title}</h1>
      {subtitle && <p className="text-p2p-muted mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const MetricCard = ({ title, value, change, trend, icon: Icon, className = "" }) => (
  <div className={`light-card light-card-hover p-6 rounded-2xl ${className}`}>
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 rounded-xl bg-p2p-teal/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-p2p-teal" strokeWidth={1.5} />
      </div>
      {change && (
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
          trend === 'up' ? 'bg-p2p-success/10 text-p2p-success' : 
          trend === 'down' ? 'bg-p2p-error/10 text-p2p-error' : 
          'bg-p2p-muted/10 text-p2p-muted'
        }`}>
          {change}
        </span>
      )}
    </div>
    <p className="text-sm text-p2p-muted mb-1">{title}</p>
    <p className="metric-value">{value}</p>
  </div>
);

export const CURRENCIES = {
  GBP: { symbol: "£", name: "British Pound" },
  USD: { symbol: "$", name: "US Dollar" },
  EUR: { symbol: "€", name: "Euro" },
  CAD: { symbol: "$", name: "Canadian Dollar" },
  AUD: { symbol: "$", name: "Australian Dollar" },
  INR: { symbol: "₹", name: "Indian Rupee" }
};

// Get currency symbol with GBP as default
export const getCurrencySymbol = (currency) => {
  if (!currency) return "£";
  return CURRENCIES[currency]?.symbol || "£";
};

export const formatCurrency = (amount, currency = "GBP") => {
  // Ensure currency defaults to GBP if null/undefined
  const safeCurrency = currency || "GBP";
  const curr = CURRENCIES[safeCurrency] || CURRENCIES.GBP;
  // FIX: Preserve negative sign for financial accuracy
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return isNegative ? `-${curr.symbol}${absAmount}` : `${curr.symbol}${absAmount}`;
};
